import { devices_config } from '@devices/index';
import type { Device } from 'src/types/Device';
import type { Group } from 'src/types/Group';
import { MOCK_LIGHT_GROUP_IDS, type MockLightCohort, mockLightId } from './mock_light_ids';

export type MockLightSpec = {
	type: string;
	mode: string;
	gen: string;
	cohort: MockLightCohort;
	rgbww_profile?: string;
	vtacMode?: 'rgb' | 'cct';
	component?: string;
	sysCh?: string[];
};

export type MockLightCatalogEntry = MockLightSpec & { id: string };

const SPECS: MockLightSpec[] = [
	// RGB + WW
	{ type: 'SPDC-0D5PE16EU', mode: 'rgbcct', gen: '2', cohort: 'rgbWw', rgbww_profile: 'rgbcct' },
	{ type: 'S3BL-C010007AEU', mode: 'rgbcct', gen: '3', cohort: 'rgbWw', vtacMode: 'rgb' },
	{ type: 'S3BL-C010007AEU', mode: 'rgb', gen: '3', cohort: 'rgbWw', vtacMode: 'rgb' },
	{ type: 'S3BL-C010007AEU', mode: 'cct', gen: '3', cohort: 'rgbWw', vtacMode: 'cct' },
	{ type: 'SHBLB-1', mode: 'color', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHBLB-1', mode: 'white', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHCB-1', mode: 'color', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHCB-1', mode: 'white', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHCL-255', mode: 'color', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHCL-255', mode: 'white', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHRGBWW-01', mode: 'color', gen: '1', cohort: 'rgbWw' },
	{ type: 'SHRGBWW-01', mode: 'white', gen: '1', cohort: 'rgbWw' },
	// RGB only
	{ type: 'SPDC-0D5PE16EU', mode: 'rgbx2light', gen: '2', cohort: 'rgb', rgbww_profile: 'rgbx2light' },
	{ type: 'SNDC-0D4P10WW', mode: 'rgb', gen: '2', cohort: 'rgb' },
	{ type: 'SNDC-0D4P10WW', mode: 'rgbw', gen: '2', cohort: 'rgb' },
	{ type: 'SHRGBW2', mode: 'color', gen: '1', cohort: 'rgb' },
	{ type: 'S3MX-0A', mode: 'rgbw', gen: '2', cohort: 'rgb', sysCh: ['rgbw:0'] },
	{ type: 'S3XT-0S', mode: 'rgb', gen: '2', cohort: 'rgb', component: 'rgb:0' },
	{ type: 'S3XT-0S', mode: 'rgbw', gen: '2', cohort: 'rgb', component: 'rgbw:0' },
	// Warm / CCT
	{ type: 'SPDC-0D5PE16EU', mode: 'cctx2', gen: '2', cohort: 'cct', rgbww_profile: 'cctx2' },
	{ type: 'S3BL-D010009AEU', mode: 'cct', gen: '3', cohort: 'cct' },
	{ type: 'S3PB-04F7045601', mode: 'cct', gen: '3', cohort: 'cct' },
	{ type: 'SHBDUO-1', mode: 'white', gen: '1', cohort: 'cct' },
	{ type: 'S3MX-0A', mode: 'cct', gen: '2', cohort: 'cct', sysCh: ['cct:0'] },
	{ type: 'S3XT-0S', mode: 'cct', gen: '2', cohort: 'cct', component: 'cct:0' },
	// Dimmer
	{ type: 'SPDC-0D5PE16EU', mode: 'light', gen: '2', cohort: 'dimmer', rgbww_profile: 'light' },
	{ type: 'SNDC-0D4P10WW', mode: 'light', gen: '2', cohort: 'dimmer' },
	{ type: 'SHRGBW2', mode: 'white', gen: '1', cohort: 'dimmer' },
	{ type: 'SHDM-1', mode: 'white', gen: '1', cohort: 'dimmer' },
	{ type: 'SHDM-2', mode: 'white', gen: '1', cohort: 'dimmer' },
	{ type: 'SHDIMW-1', mode: 'white', gen: '1', cohort: 'dimmer' },
	{ type: 'SHVIN-1', mode: 'white', gen: '1', cohort: 'dimmer' },
	{ type: 'SH2LED-1', mode: 'white', gen: '1', cohort: 'dimmer' },
	{ type: 'SNDM-0013US', mode: 'white', gen: '2', cohort: 'dimmer' },
	{ type: 'SNDM-00100WW', mode: 'light', gen: '3', cohort: 'dimmer' },
	{ type: 'SNDM-9995WW', mode: 'white', gen: '3', cohort: 'dimmer' },
	{ type: 'SPDM-001PE01EU', mode: 'light', gen: '2', cohort: 'dimmer' },
	{ type: 'SPDM-002PE01EU', mode: 'light', gen: '2', cohort: 'dimmer' },
	{ type: 'S3DM-0A1WW', mode: 'light', gen: '3', cohort: 'dimmer' },
	{ type: 'S3DM-0A101WWL', mode: 'light', gen: '3', cohort: 'dimmer' },
	{ type: 'S3DM-0010WW', mode: 'light', gen: '3', cohort: 'dimmer' },
	{ type: 'S4DM-0A102US', mode: 'light', gen: '4', cohort: 'dimmer' },
	{ type: 'S4DM-0A101WWL', mode: 'light', gen: '4', cohort: 'dimmer' },
	{ type: 'S4DM-0010WW', mode: 'light', gen: '4', cohort: 'dimmer' },
	{ type: 'SPCC-001PE10EU', mode: 'light', gen: '3', cohort: 'dimmer' },
	{ type: 'ZW-DIMMER', mode: 'white', gen: 'GZ', cohort: 'dimmer' },
	{ type: 'S3MX-0A', mode: 'light', gen: '2', cohort: 'dimmer', sysCh: ['light:0'] },
	{ type: 'S3XT-0S', mode: 'light', gen: '2', cohort: 'dimmer', component: 'light:0' },
];

export const MOCK_LIGHT_CATALOG: MockLightCatalogEntry[] = SPECS.map((spec) => ({
	...spec,
	id: mockLightId(spec.type, spec.mode),
}));

export const MOCK_LIGHT_COHORT_LABELS: Record<MockLightCohort, string> = {
	rgbWw: 'RGB + WW',
	rgb: 'RGB only',
	cct: 'Warm / CCT',
	dimmer: 'Dimmer',
};

const RGBWW_SYS_CH: Record<string, string[]> = {
	rgbcct: ['rgb:0', 'cct:0'],
	rgbx2light: ['rgb:0', 'light:0'],
	cctx2: ['cct:0', 'cct:1'],
};

const lightChannelKeys = (count: number): string[] =>
	Array.from({ length: count }, (_, index) => `light:${index}`);

const lightBlock = (extras: Record<string, unknown> = {}) => ({
	id: 0,
	output: true,
	ison: true,
	brightness: 80,
	gain: 50,
	...extras,
});

const gen1Light = (mode: string) => ({
	ison: true,
	mode,
	brightness: 80,
	gain: 50,
	red: 255,
	green: 80,
	blue: 0,
	white: 40,
	temp: 4000,
	ct: 4000,
});

export const getFamilyLightModeKeys = (): Array<{ type: string; mode: string }> =>
	Object.entries(
		devices_config as Record<string, { family?: string; modes?: Record<string, unknown> }>,
	)
		.filter(([, config]) => config?.family === 'light')
		.flatMap(([type, config]) => Object.keys(config?.modes || {}).map((mode) => ({ type, mode })));

export const getCatalogCoverageKeys = (): Array<{ type: string; mode: string }> =>
	MOCK_LIGHT_CATALOG.map((entry) => ({ type: entry.type, mode: entry.mode }));

export const getMockLightIdsByCohort = (cohort: MockLightCohort): string[] =>
	MOCK_LIGHT_CATALOG.filter((entry) => entry.cohort === cohort).map((entry) => entry.id);

const friendlyName = (type: string): string =>
	(devices_config as Record<string, { friendly_name?: string }>)?.[type]?.friendly_name || type;

const channelsCount = (type: string, mode: string): number =>
	(devices_config as Record<string, { modes?: Record<string, { channels?: number }> }>)?.[type]
		?.modes?.[mode]?.channels || 1;

export const buildMockLightDevice = (
	entry: MockLightCatalogEntry,
	roomId: number,
	position: number,
): Device =>
	({
		id: entry.id,
		type: entry.type,
		name: `Mock · ${friendlyName(entry.type)} · ${entry.mode}`,
		gen: entry.gen,
		category: 'light',
		mode: entry.mode,
		channel: 0,
		channels_count: channelsCount(entry.type, entry.mode),
		room_id: roomId,
		position,
		image: entry.type,
		cloud_online: true,
		is_mock: true,
		...(entry.rgbww_profile ? { rgbww_profile: entry.rgbww_profile } : {}),
		...(entry.component ? { component: entry.component } : {}),
	}) as Device;

const fillLightOutputs = (status: Record<string, unknown>, count: number) => {
	const keys = lightChannelKeys(count);
	status.sys = { ch: keys };
	keys.forEach((key, index) => {
		status[key] = lightBlock({ id: index, brightness: Math.max(20, 80 - index * 12) });
	});
};

export const buildMockLightStatus = (entry: MockLightCatalogEntry): Record<string, unknown> => {
	const status: Record<string, unknown> = { online: true };
	const count = channelsCount(entry.type, entry.mode);

	if (entry.gen === '1') {
		status.mode = entry.mode;
		status.lights = Array.from({ length: count }, () => gen1Light(entry.mode));
		return status;
	}

	if (entry.gen === 'GZ') {
		status.mode = entry.mode;
		status.lights = Array.from({ length: count }, () => gen1Light(entry.mode));
		status['light:100'] = lightBlock();
		return status;
	}

	const sysCh =
		entry.sysCh ||
		(entry.rgbww_profile === 'light' ? lightChannelKeys(count) : undefined) ||
		(entry.rgbww_profile ? RGBWW_SYS_CH[entry.rgbww_profile] : undefined) ||
		(entry.component ? [entry.component] : undefined);

	if (sysCh) {
		status.sys = { ch: sysCh };
	}

	if (entry.vtacMode) {
		status.mode = entry.vtacMode;
		status['rgbcct:0'] = lightBlock({
			rgb: [255, 80, 0],
			ct: 4000,
			mode: entry.vtacMode,
		});
		return status;
	}

	if (entry.rgbww_profile === 'rgbcct') {
		status['rgbcct:0'] = lightBlock({ rgb: [255, 80, 0], ct: 4000, mode: 'rgb' });
		status['rgb:0'] = lightBlock({ rgb: [255, 80, 0] });
		status['cct:0'] = lightBlock({ ct: 4000 });
		return status;
	}

	if (entry.rgbww_profile === 'rgbx2light') {
		status['rgbx2light:0'] = lightBlock({ rgb: [255, 80, 0] });
		status['rgb:0'] = lightBlock({ rgb: [255, 80, 0] });
		status['light:0'] = lightBlock();
		return status;
	}

	if (entry.rgbww_profile === 'cctx2') {
		status['cctx2:0'] = lightBlock({ ct: 4000 });
		status['cct:0'] = lightBlock({ ct: 4000 });
		status['cct:1'] = lightBlock({ id: 1, ct: 4000 });
		return status;
	}

	const component = entry.component?.split(':')[0] || entry.mode;
	if (component === 'rgbw') {
		status['rgbw:0'] = lightBlock({ rgb: [255, 80, 0], white: 40 });
	} else if (component === 'rgb') {
		status['rgb:0'] = lightBlock({ rgb: [255, 80, 0] });
	} else if (component === 'cct') {
		status['cct:0'] = lightBlock({ ct: 4000 });
	} else if (component === 'rgbcct') {
		status['rgbcct:0'] = lightBlock({ rgb: [255, 80, 0], ct: 4000, mode: 'rgb' });
	} else {
		fillLightOutputs(status, count);
	}

	return status;
};

export const buildMockLightSettings = (entry: MockLightCatalogEntry): Record<string, unknown> => {
	const settings: Record<string, unknown> = {};

	if (entry.vtacMode) {
		settings['rgbcct:0'] = { id: 0, mode: entry.vtacMode };
	}

	if (entry.rgbww_profile || entry.type === 'SNDC-0D4P10WW') {
		settings.sys = { device: { profile: entry.rgbww_profile || entry.mode } };
	}

	if (entry.gen === '1') {
		settings.mode = entry.mode;
	}

	return settings;
};

export const buildMockLightGroups = (roomId: number): Record<number, Group> => {
	const groups = {} as Record<number, Group>;
	(Object.keys(MOCK_LIGHT_GROUP_IDS) as MockLightCohort[]).forEach((cohort, position) => {
		const id = MOCK_LIGHT_GROUP_IDS[cohort];
		groups[id] = {
			id,
			name: `Mock · ${MOCK_LIGHT_COHORT_LABELS[cohort]}`,
			type: 'light',
			devices: getMockLightIdsByCohort(cohort),
			room_id: roomId,
			position,
			image: '',
		};
	});
	return groups;
};
